/* eslint-disable object-curly-newline */
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { INativeSampleData } from 'src/app/shared/models/i-native-sample.modal';
import { NativeSampleType } from 'src/app/shared/models/i-precast-sampletypes.modal';
import { DecimalValidator, negativedecimalValidator } from 'src/app/shared/validators/one-decimal.validator';
import { IDropdown } from '../../../../shared/models/i-dropdown-type.modal';
import { PrecastService } from '../../../../shared/services/precast.service';
import { messagesConfig } from '../../../../shared/constants/message.constants';
import { DialogService } from '../../../../shared/services/dialog.service';
import { TechnicalInstructionsComponent } from '../technical-instructions/technical-instructions.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-sample-data-nativesoil',
    templateUrl: 'sample-data-nativeSoil.component.html',
    styleUrls: ['sample-data-nativeSoil.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SampleDataNativeSoilComponent implements OnInit, OnChanges {
  @Output() sampleDataNativeData: EventEmitter<INativeSampleData> = new EventEmitter();
  @Input() data: any;
  @Input() nativeSampleType : NativeSampleType [] = [];
  @Input() set successfullSubmission(value: boolean) {
    if (value) {
      this.sampleDataNativeSoilForm.reset();
      const formLength = this.getSampleControls.controls.length;
      for (let i = 0; i < formLength; i++) {
        this.removeItem(0);
      }
      this.addSample();
    }
  }

  sampleDataNativeSoilForm: FormGroup;
  step = -1;
  public nativeSoilSampleTypes: IDropdown[];
  currentDate = new Date(Date.now());
  selectedIndex = -1;
  compactionEffort = '';
  newControlDisable = false;
  makeSampleEditable = true;
  readytoEmit = false;
  isDepthRequired = false;
  isSpecifiedDisabled = true;
  showDepthError = false;
  depthErrorMessage = '';
  @Input() set makeEditable(value: boolean) {
    this.makeSampleEditable = value;
    if (!value) {
      this.newControlDisable = true;
    }
  }
  isEdit = false;

  constructor(
    private precastService: PrecastService,
    private dialogService: DialogService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.sampleDataNativeSoilForm = this.fb?.group({
      samples: this.fb?.array([]),
    });
  }

  ngOnInit(): void {
    this.precastService?.fetchNativeSampleTypes()?.subscribe({
      next: (response) => {
        // TODO: used regex to identify edit mode as a hotfix, need to find a better way to identify edit mode in future
        const guidRegex = /\/edit\/[0-9a-fA-F-]{36}(?=\?|$)/;
        this.isEdit = guidRegex.test(this.router.url);

        this.nativeSoilSampleTypes = response;
        const nativesoilType = this.nativeSoilSampleTypes.find((x) => x.label === 'Tube');

        if (this.data) {
          const sampleLength = this.data.length;
          if (sampleLength > 0) {
            this.step = -1;
            for (let i = 0; i < sampleLength; i++) {
              this.getSampleControls.push(this.createItem());
              if (this.data[i].sampleTypeMasterId === nativesoilType?.id) {
                this.removeValidationForTube(this.getSampleControls.controls[i]);
              } else {
                this.setCompactionEffortRequiredorNot(i, this.getSampleControls.controls[i]);
                this.setValidatorsForBulk(this.getSampleControls.controls[i]);
              }
            }
          } else {
            this.step = 0;
          }
          this.data.forEach((elem: any, i: number) => {
            this.disabledSpecifiedMC(this.getSampleControls.controls[i]);
            if (elem.moistureOptimum) {
              elem.highestMoistureContent = '1';
            } else if (elem.moistureAsReceived) {
              elem.highestMoistureContent = '2';
            } else if (elem.specifiedMoistureContent) {
              elem.highestMoistureContent = '3';
              this.enableSpecifiedMC(this.getSampleControls.controls[i]);
            } else {
              elem.highestMoistureContent = null;
            }

            if(elem.isSinglePointProctor) {
              elem.highestMoistureContent = '2';
            }
          });

          const patchValue = {
            samples: this.data,
          };
          let selectedCompactionEffortIndex = -1;
          patchValue.samples.forEach((element: any, i: number) => {
            const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
            const currentControl = controls[i];
            if (element?.applyCompactionEffort) {
              selectedCompactionEffortIndex = i;
              this.compactionEffort = element.compactionEffort;
              this.newControlDisable = true;
            }
            if (element?.stockPile) {
              currentControl.get('sampleDepth')?.patchValue(null);
              currentControl.get('sampleDepth')?.disable();
              currentControl.get('sampleEndDepth')?.patchValue(null);
              currentControl.get('sampleEndDepth')?.disable();
            }
            if(element?.isSinglePointProctor) {
              this.setIsSinglePointProctorValue(element.isSinglePointProctor, currentControl);
            }
          });
          if (selectedCompactionEffortIndex > -1) {
            this.setApplyCompactionEffort(selectedCompactionEffortIndex);
          }
          this.sampleDataNativeSoilForm.patchValue(patchValue);
        } else {
          this.step = 0;
          this.getSampleControls.push(this.createItem());
        }
        this.sampleDataNativeData?.emit({
          value: this.sampleDataNativeSoilForm?.value?.samples,
          valid: this.sampleDataNativeSoilForm?.valid,
          dirty: this.sampleDataNativeSoilForm?.dirty,
        });
        this.readytoEmit = true;
      },
    });
    this.sampleDataNativeSoilForm?.valueChanges?.subscribe((frmGrp) => {
      if (this.readytoEmit) {
        this.sampleDataNativeData?.emit({
          value: frmGrp?.samples,
          valid: this.sampleDataNativeSoilForm?.valid,
          dirty: this.sampleDataNativeSoilForm?.dirty,
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['makeEditable']) {
      this.updateFieldStates();
    }
  }

  private updateFieldStates(): void {
    const { controls } = this.getSampleControls;
    controls.forEach((control) => {
      if (this.makeSampleEditable) {
        control.get('sampleTypeMasterId')?.enable();
        control.get('maxDryDensity')?.enable();
        control.get('optimumMoistureContent')?.enable();
        control.get('compactionEffort')?.enable();
        control.get('applyCompactionEffort')?.enable();
        control.get('highestMoistureContent')?.enable();
        control.get('moistureOptimum')?.enable();
        control.get('moistureAsReceived')?.enable();
        control.get('specifiedMoistureContent')?.enable();
      } else {
        control.get('sampleTypeMasterId')?.disable();
        control.get('maxDryDensity')?.disable();
        control.get('optimumMoistureContent')?.disable();
        control.get('compactionEffort')?.disable();
        control.get('applyCompactionEffort')?.disable();
        control.get('highestMoistureContent')?.disable();
        control.get('moistureOptimum')?.disable();
        control.get('moistureAsReceived')?.disable();
        control.get('specifiedMoistureContent')?.disable();
      }

      control.get('sampleTypeMasterId')?.updateValueAndValidity();
      control.get('maxDryDensity')?.updateValueAndValidity();
      control.get('optimumMoistureContent')?.updateValueAndValidity();
      control.get('compactionEffort')?.updateValueAndValidity();
      control.get('applyCompactionEffort')?.updateValueAndValidity();
      control.get('highestMoistureContent')?.updateValueAndValidity();
      control.get('moistureOptimum')?.updateValueAndValidity();
      control.get('moistureAsReceived')?.updateValueAndValidity();
      control.get('specifiedMoistureContent')?.updateValueAndValidity();
    });

    this.cdr.detectChanges();
  }

  setApplyCompactionEffort(index: number): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    for (let i = 0; i < controls.length; i++) {
      if (i !== index) {
        const nativesoilType = this.nativeSoilSampleTypes.find((x) => x.label === 'Tube');
        if (controls[i].get('sampleTypeMasterId')?.value !== nativesoilType?.id) {
          controls[i].get('applyCompactionEffort')?.patchValue(this.newControlDisable);
          controls[i].get('applyCompactionEffort')?.disable();
          controls[i].get('applyCompactionEffort')?.updateValueAndValidity();
        }
      }
    }
  }

  enableSpecifiedMC(sample: any): void {
    const currentSample = sample as FormGroup;
    currentSample.get('specifiedMoistureContent')?.enable();
  }

  disabledSpecifiedMC(sample: any): void {
    const currentSample = sample as FormGroup;
    currentSample.get('specifiedMoistureContent')?.disable();
  }

  removeApplyCompactionEffortDisable(): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    for (let i = 0; i < controls.length; i++) {
      const nativesoilType = this.nativeSoilSampleTypes.find((x) => x.label === 'Tube');
      if (controls[i].get('sampleTypeMasterId')?.value !== nativesoilType?.id) {
        controls[i].get('applyCompactionEffort')?.patchValue(false);
        controls[i].get('applyCompactionEffort')?.enable();
        controls[i].get('applyCompactionEffort')?.updateValueAndValidity();
      }
    }
  }

  private setCompactionEffortRequiredorNot(i:number, sample: any): void {
    const currentSample = sample as FormGroup;
    if (this.data[i].dump) {
      currentSample.get('compactionEffort')?.removeValidators(Validators.required);
    } else {
      currentSample.get('compactionEffort')?.setValidators(Validators.required);
    }
    currentSample.get('compactionEffort')?.updateValueAndValidity();
  }

  private setValidatorsForBulk(sample: any): void {
    const currentSample = sample as FormGroup;
    currentSample.get('highestMoistureContent')?.setValidators(Validators.required);
    currentSample.get('highestMoistureContent')?.updateValueAndValidity();
    currentSample.get('proctorType')?.setValidators(Validators.required);
    currentSample.get('proctorType')?.updateValueAndValidity();
    currentSample.get('stockPile')?.enable();
    currentSample.get('stockPile')?.updateValueAndValidity();
  }

  public addSample(): void {
    if (this.getSampleControls.length < 50) {
      this.getSampleControls.push(this.createItem());
      // eslint-disable-next-line no-plusplus
      this.step = this.getSampleControls.length - 1;
    } else {
      this.dialogService.open(messagesConfig.maxSampleWarning);
    }
  }

  public removeItem(index: number) {
    this.getSampleControls.removeAt(index);
  }

  public setStep(index: number) {
    this.step = index;
  }

  public onNativeSoilSampleTypeChange(sample: any): void {
    const currentSample = sample as FormGroup;
    this.resetHighestMoistureContent(currentSample);
    switch (currentSample.get('highestMoistureContent')?.value) {
      case '1':
        currentSample.get('moistureOptimum')?.setValue(true);
        currentSample.get('specifiedMoistureContent')?.setValue(null);
        currentSample.get('moistureAsReceived')?.setValue(false);
        currentSample.get('specifiedMoistureContent')?.disable();
        break;
      case '2':
        currentSample.get('moistureOptimum')?.setValue(false);
        currentSample.get('specifiedMoistureContent')?.setValue(null);
        currentSample.get('moistureAsReceived')?.setValue(true);
        currentSample.get('specifiedMoistureContent')?.disable();
        break;
      case '3':
        currentSample.get('specifiedMoistureContent')?.enable();
        currentSample.get('moistureOptimum')?.setValue(false);
        currentSample
          .get('specifiedMoistureContent')
          ?.setValidators([Validators.required, Validators.maxLength(5), DecimalValidator()]);
        currentSample.get('specifiedMoistureContent')?.updateValueAndValidity();
        currentSample.get('moistureAsReceived')?.setValue(false);
        break;
      default:
        break;
    }
  }

  removeValidationForTube(sample: any): void {
    const currentSample = sample as FormGroup;
    currentSample.get('compactionEffort')?.removeValidators(Validators.required);
    currentSample.get('compactionEffort')?.setValue('');
    currentSample.get('compactionEffort')?.updateValueAndValidity();
    currentSample.get('highestMoistureContent')?.removeValidators(Validators.required);
    currentSample.get('highestMoistureContent')?.setValue(null);
    currentSample.get('highestMoistureContent')?.updateValueAndValidity();
    currentSample.get('specifiedMoistureContent')?.removeValidators(Validators.required);
    currentSample.get('specifiedMoistureContent')?.setValue(null);
    currentSample.get('specifiedMoistureContent')?.updateValueAndValidity();
    currentSample.get('proctorType')?.removeValidators(Validators.required);
    currentSample.get('proctorType')?.setValue(null);
    currentSample.get('proctorType')?.updateValueAndValidity();
    currentSample.get('moistureOptimum')?.setValue(false);
    currentSample.get('moistureOptimum')?.updateValueAndValidity();
    currentSample.get('moistureAsReceived')?.setValue(false);
    currentSample.get('moistureAsReceived')?.updateValueAndValidity();
    currentSample.get('maxDryDensity')?.setValue(null);
    currentSample.get('maxDryDensity')?.updateValueAndValidity();
    currentSample.get('optimumMoistureContent')?.setValue(null);
    currentSample.get('optimumMoistureContent')?.updateValueAndValidity();
    currentSample.get('stockPile')?.patchValue(false);
    currentSample.get('stockPile')?.disable();
    currentSample.get('stockPile')?.updateValueAndValidity();
    currentSample.get('sampleDepth')?.enable();
    currentSample.get('sampleDepth')?.updateValueAndValidity();
    currentSample.get('sampleEndDepth')?.enable();
    currentSample.get('sampleEndDepth')?.updateValueAndValidity();
    currentSample.get('dump')?.setValue(false);
    currentSample.get('dump')?.updateValueAndValidity();
  }

  public onNativeSoilTypeChange(sample: any): void {
    const nativesoilType = this.nativeSoilSampleTypes.find((x) => x.label === 'Tube');
    const currentSample = sample as FormGroup;
    if (currentSample.get('sampleTypeMasterId')?.value === nativesoilType?.id) {
      this.removeValidationForTube(sample);
      if (!currentSample.get('applyCompactionEffort')?.disabled && currentSample.get('applyCompactionEffort')?.value) {
        this.newControlDisable = false;
        this.compactionEffort = '';
        this.removeApplyCompactionEffortDisable();
      }

      currentSample.get('applyCompactionEffort')?.patchValue(false);
      currentSample.get('applyCompactionEffort')?.enable();
      currentSample.get('applyCompactionEffort')?.updateValueAndValidity();
    } else {
      this.setValidatorsForBulk(sample);
      if (this.newControlDisable) {
        currentSample.get('compactionEffort')?.setValue(this.compactionEffort);
        currentSample.get('compactionEffort')?.updateValueAndValidity();
        currentSample.get('applyCompactionEffort')?.patchValue(this.newControlDisable);
        currentSample.get('applyCompactionEffort')?.disable();
        currentSample.get('applyCompactionEffort')?.updateValueAndValidity();
      }
    }
    // this.setStep(index);
  }

  get getSampleControls(): FormArray {
    return this.sampleDataNativeSoilForm?.controls['samples'] as FormArray;
  }

  public checksoilType(index: number): string {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    return controls[index].get('sampleTypeMasterId')?.value;
  }

  public onStockPileCheckBoxClicked(currentIndex: number): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    const currentControl = controls[currentIndex];
    if (currentControl.get('stockPile')?.value) {
      currentControl.get('sampleDepth')?.patchValue(null);
      currentControl.get('sampleDepth')?.disable();
      currentControl.get('sampleEndDepth')?.patchValue(null);
      currentControl.get('sampleEndDepth')?.disable();
    } else {
      currentControl.get('sampleDepth')?.clearValidators();
      currentControl.get('sampleDepth')?.enable();
      currentControl.get('sampleEndDepth')?.clearValidators();
      currentControl.get('sampleEndDepth')?.enable();
    }
  }

  public onChangeIsSinglePointProctor(index: number): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    const currentControl = controls[index];
    const isChecked = currentControl.get('isSinglePointProctor')?.value;
    this.setIsSinglePointProctorValue(isChecked, currentControl);
  }

  private setIsSinglePointProctorValue(isChecked: boolean, currentControl: AbstractControl): void {
    if (isChecked) {
      currentControl.get('maxDryDensity')?.setValue(null); 
      currentControl.get('maxDryDensity')?.disable();     
      currentControl.get('optimumMoistureContent')?.setValue(null); 
      currentControl.get('optimumMoistureContent')?.disable();
      const highestMoistureControl = currentControl.get('highestMoistureContent');
      highestMoistureControl?.setValue('2', { emitEvent: true });
      highestMoistureControl?.markAsDirty();
      highestMoistureControl?.markAsTouched();
      currentControl.get('moistureAsReceived')?.setValue(false);
      currentControl.get('moistureOptimum')?.setValue(false);
      currentControl.get('specifiedMoistureContent')?.setValue(null);
      currentControl.get('specifiedMoistureContent')?.disable();
    } else {
      currentControl.get('maxDryDensity')?.enable();
      currentControl.get('optimumMoistureContent')?.enable();
      currentControl.get('highestMoistureContent')?.setValue(null);
      currentControl.get('specifiedMoistureContent')?.enable();
    }
  }

  public isSinglePointProctorChecked(sample: AbstractControl): boolean {
    return sample.get('isSinglePointProctor')?.value === true;
  }

  public onChangeUseCompactionCheckBox(currentIndex: number): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    const currentControl = controls[currentIndex];
    this.compactionEffort = currentControl.get('applyCompactionEffort')?.value
      ? currentControl.get('compactionEffort')?.value
      : '';
    this.newControlDisable = currentControl.get('applyCompactionEffort')?.value;
    this.selectedIndex = this.newControlDisable ? currentIndex : -1;
    for (let i = 0; i < controls.length; i++) {
      if (i !== currentIndex) {
        const nativesoilType = this.nativeSoilSampleTypes.find((x) => x.label === 'Tube');
        if (controls[i].get('sampleTypeMasterId')?.value !== nativesoilType?.id) {
          controls[i].get('compactionEffort')?.patchValue(this.compactionEffort);
          controls[i].get('applyCompactionEffort')?.patchValue(this.newControlDisable);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          this.newControlDisable
            ? controls[i].get('applyCompactionEffort')?.disable()
            : controls[i].get('applyCompactionEffort')?.enable();
        }
      }
    }
  }

  public checkProctorTypeIsValid(sample: any): string {
    const currentSample = sample as FormGroup;
    return currentSample.get('proctorType')?.touched && currentSample.get('proctorType')?.hasError('required')
      ? 'Please select Proctor Type.'
      : '';
  }

  public checkhighestMoistureContentIsValid(sample: any): string {
    const currentSample = sample as FormGroup;
    return currentSample.get('highestMoistureContent')?.touched
      && currentSample.get('highestMoistureContent')?.hasError('required')
      ? 'Please select Highest Moisture Content Type.'
      : '';
  }

  public openInstructionsDialog() {
    this.dialog.open(TechnicalInstructionsComponent, {
      data: {},
      disableClose: true,
    });
  }

  private createItem(): FormGroup {
    return this.fb?.group({
      id: [null],
      sampleTypeMasterId: [
        {
          value: '',
          disabled: !this.makeSampleEditable,
        },
        Validators.required,
      ],
      sampleId: ['', Validators.required],
      proctorType: ['', Validators.required],
      collectionDate: [''],
      sampleDepth: [null],
      sampleEndDepth: [null],
      soilDescription: [''],
      stockPile: false,
      maxDryDensity: [
        {
          value: null,
          disabled: !this.makeSampleEditable,
        },
        DecimalValidator(),
      ],
      optimumMoistureContent: [
        {
          value: null,
          disabled: !this.makeSampleEditable,
        },
        DecimalValidator(),
      ],
      compactionEffort: [
        {
          value: this.compactionEffort,
          disabled: !this.makeSampleEditable,
        },
        Validators.required,
      ],
      applyCompactionEffort: [
        {
          value: this.newControlDisable,
          disabled: this.newControlDisable,
        },
      ],
      highestMoistureContent: [
        {
          value: null,
          disabled: !this.makeSampleEditable,
        },
        Validators.required,
      ],
      moistureOptimum: [
        {
          value: false,
          disabled: !this.makeSampleEditable,
        },
      ],
      moistureAsReceived: [
        {
          value: false,
          disabled: !this.makeSampleEditable,
        },
      ],
      specifiedMoistureContent: [
        {
          value: '',
          disabled: !this.makeSampleEditable,
        },
      ],
      dump: [
        {
          value: false,
          disabled: !this.makeSampleEditable,
        },
      ],
      isSinglePointProctor: [
        {
          value: false,
          disabled: !this.makeSampleEditable,
        },
      ],
    });
  }

  private resetHighestMoistureContent(currentSample: FormGroup): void {
    currentSample.get('specifiedMoistureContent')?.removeValidators(Validators.required);
    currentSample.get('specifiedMoistureContent')?.updateValueAndValidity();
  }

  handlePanelClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  depthValidation(index: number, controlName: string, alternateControl: string): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    const currentControl = controls[index];
    const controlDepth = currentControl.get(controlName)?.value;
    const alternateControlDepth = currentControl.get(alternateControl)?.value;

    if (alternateControlDepth !== null && alternateControlDepth !== '') {
      if (controlDepth === null || controlDepth === '') {
        currentControl.get(controlName)?.setValidators(Validators.required);
        currentControl.get(controlName)?.updateValueAndValidity();
      } else {
        currentControl.get(controlName)?.clearValidators();
        currentControl.get(controlName)?.updateValueAndValidity();
      }

      if (controlDepth !== '' && controlDepth !== null) {
        currentControl
          .get(alternateControl)
          ?.setValidators([
            negativedecimalValidator(),
            controlName === 'sampleDepth' ? Validators.min(controlDepth) : Validators.max(controlDepth),
          ]);
        currentControl.get(alternateControl)?.updateValueAndValidity();
      }
    } else {
      currentControl.get(controlName)?.clearValidators();
      currentControl.get(controlName)?.updateValueAndValidity();
      currentControl.get(alternateControl)?.clearValidators();
      currentControl.get(alternateControl)?.updateValueAndValidity();
    }

    if (controlDepth !== '' && controlDepth !== null && (alternateControlDepth === '' || alternateControlDepth === null)) {
      currentControl
        .get(alternateControl)
        ?.setValidators([
          negativedecimalValidator(),
          controlName === 'sampleDepth' ? Validators.min(controlDepth) : Validators.max(controlDepth),
        ]);
      currentControl.get(alternateControl)?.updateValueAndValidity();
    }
  }

  decimalFilter(event: KeyboardEvent) {
    const inputElement = event.target as HTMLInputElement;
    const { key } = event;
    const inputValue = inputElement.value;

    // Allow control keys (Backspace, Delete, Arrow keys, Tab)
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
      return;
    }

    // Allow '-' only at the start
    if (key === '-' && inputValue.length === 0) {
      return;
    }

    // Prevent '-' anywhere except the first character
    if (key === '-' && inputValue.length > 0) {
      event.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (key === '.' && inputValue.includes('.')) {
      event.preventDefault();
      return;
    }

    // Simulate the new value after keypress
    const newValue = inputValue + key;

    // Regex to allow:
    // - Up to 5 digits before decimal (4 if negative)
    // - Max 2 digits after decimal
    // - Total length up to 7 characters
    const decimalRegex = /^-?\d{1,5}(\.\d{0,2})?$/;

    // Show an error if the input ends with '.'
    if (newValue.endsWith('.')) {
      inputElement.setCustomValidity('Invalid input: A number must follow the decimal.');
      inputElement.reportValidity();
    } else {
      inputElement.setCustomValidity(''); // Clear error message
    }

    // **New Validation: Prevent entering more than 5 whole number digits**
    const isWholeNumber = /^-?\d{6,}$/.test(newValue); // More than 5 digits before decimal
    if (isWholeNumber) {
      inputElement.setCustomValidity('The input cannot be more than 5 digits, until it includes up to 2 decimal places.');
      inputElement.reportValidity();
      event.preventDefault();
      return;
    }

    // Block invalid inputs
    if (!decimalRegex.test(newValue) || newValue.length > 7) {
      event.preventDefault();
    }
  }

  clearDepthValidators(index: number): void {
    const { controls } = this.sampleDataNativeSoilForm.controls['samples'] as FormArray;
    const currentControl = controls[index];
    currentControl.get('sampleDepth')?.clearValidators();
    currentControl.get('sampleEndDepth')?.clearValidators();
    currentControl.get('sampleDepth')?.updateValueAndValidity();
    currentControl.get('sampleEndDepth')?.updateValueAndValidity();
  }

  public depthError(sample: any): string {
    const currentSample = sample as FormGroup;
    let requiredError;
    requiredError = currentSample.get('sampleDepth')?.hasError('required');
    const startDepth = currentSample.get('sampleDepth')?.value;
    const endDepth = currentSample.get('sampleEndDepth')?.value;
    let message = '';
    if (startDepth === '' && endDepth === '') {
      return message;
    }

    if (!requiredError) {
      requiredError = currentSample.get('sampleEndDepth')?.hasError('required');
    }

    if (requiredError) {
      message = 'Please enter the depth value, it cannot be blank.';
    } else if (
      startDepth !== null
      && endDepth !== null
      && Number(endDepth) <= Number(startDepth)
      && !currentSample.get('stockPile')?.value
    ) {
      requiredError = currentSample.get('sampleEndDepth')?.hasError('min');
      message = 'The End depth cannot be less than or equal to Start depth.';
      currentSample.get('sampleEndDepth')?.setValidators([Validators.min(Number(startDepth + 1))]);
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
    } else if (startDepth !== null && endDepth !== null && !currentSample.get('stockPile')?.value) {
      currentSample.get('sampleEndDepth')?.setValidators(DecimalValidator());
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
    } else {
      currentSample.get('sampleEndDepth')?.clearValidators();
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
    }
    currentSample.updateValueAndValidity();
    return message;
  }

  public activeDepthError(sample: any): string {
    const currentSample = sample as FormGroup;
    const startDepth = currentSample.get('sampleDepth')?.value;
    const endDepth = currentSample.get('sampleEndDepth')?.value;
    let message = '';

    const newStartValue = startDepth !== null ? String(startDepth) : '';
    const newEndValue = endDepth !== null ? String(endDepth) : '';
    const invalidStartDepth = /^-0(\.0+)?$/;
    const startdot = newStartValue.endsWith('.');
    const enddot = newEndValue.endsWith('.');
    const isStartWholeNumber = /^-?\d{6,}$/.test(newStartValue);
    const isEndWholeNumber = /^-?\d{6,}$/.test(newEndValue);
    if (startDepth === '' && endDepth === '') {
      return message;
    }

    const startDepthNum = Number(startDepth);
    const endDepthNum = Number(endDepth);

    if (endDepth && (startDepth === null || startDepth === '')) {
      currentSample.get('sampleDepth')?.setValidators(Validators.required);
      currentSample.get('sampleDepth')?.updateValueAndValidity();
      message = 'For Depth range, Start Depth and End Depth are required.';
    } else if (startDepth !== null && endDepth !== null && startDepth !== '' && endDepth !== '') {
      // Check if endDepth is less than or equal to startDepth
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(startDepthNum) && !isNaN(endDepthNum) && endDepthNum <= startDepthNum) {
        message = 'The End Depth cannot be less than or equal to Start Depth.';
        currentSample.get('sampleEndDepth')?.setValidators([Validators.min(startDepthNum + 1)]);
        currentSample.get('sampleEndDepth')?.updateValueAndValidity();
      }
    } else if (invalidStartDepth.test(startDepth) || startDepth === '-0.') {
      currentSample.get('sampleDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleDepth')?.updateValueAndValidity();
      message = 'Start Depth cannot be -0.';
    } else if (startdot) {
      currentSample.get('sampleDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleDepth')?.updateValueAndValidity();
      message = 'The input cannot be more than 5 digits, until it includes up to 2 decimal places.';
    } else if (enddot) {
      currentSample.get('sampleEndDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
      message = 'The input cannot be more than 5 digits, until it includes up to 2 decimal places.';
    } else if (startDepth === '-') {
      currentSample.get('sampleDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleDepth')?.updateValueAndValidity();
      message = 'Invalid input: Hyphen must follow the number.';
    } else if (endDepth === '-') {
      currentSample.get('sampleEndDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
      message = 'Invalid input: Hyphen must follow the number.';
    } else if (isStartWholeNumber) {
      currentSample.get('sampleDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleDepth')?.updateValueAndValidity();
      message = 'The input cannot be more than 5 digits, until it includes up to 2 decimal places.';
    } else if (isEndWholeNumber) {
      currentSample.get('sampleEndDepth')?.setValidators(negativedecimalValidator);
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
      message = 'The input cannot be more than 5 digits, until it includes up to 2 decimal places.';
    } else {
      currentSample.get('sampleEndDepth')?.clearValidators();
      currentSample.get('sampleEndDepth')?.updateValueAndValidity();
      currentSample.get('sampleDepth')?.clearValidators();
      currentSample.get('sampleDepth')?.updateValueAndValidity();
    }

    return message;
  }

  public optimumMoistureContentError(sample: any): string {
    const currentSample = sample as FormGroup;
    const value = currentSample.get('optimumMoistureContent')?.value;
    let message = '';
    message = this.checkForValidDecimal(value);
    return message;
  }

  public maxDryDensityError(sample: any): string {
    const currentSample = sample as FormGroup;
    const value = currentSample.get('maxDryDensity')?.value;
    let message = '';
    message = this.checkForValidDecimal(value);
    return message;
  }

  public specifiedMoistureContentError(sample: any): string {
    const currentSample = sample as FormGroup;
    const value = currentSample.get('specifiedMoistureContent')?.value;
    const hm = currentSample.get('highestMoistureContent')?.value;
    let message = '';
    if (Number(hm) === 3) {
      message = this.checkForValidDecimal(value);
    }
    return message;
  }

  onDumpCheckBoxClicked(sampleIndex: number): void {
    const sampleControl = this.getSampleControls.at(sampleIndex) as FormGroup;
    const dumpControl = sampleControl.get('dump');
    const compactionEffortControl = sampleControl.get('compactionEffort');
    if (dumpControl?.value) {
      compactionEffortControl?.clearValidators();
    } else {
      compactionEffortControl?.setValidators(Validators.required);
    }
    compactionEffortControl?.updateValueAndValidity();
  }

  checkForValidDecimal(value: string): string {
    let message = '';
    if (typeof value === 'string') {
      const decimalIndex = value.indexOf('.');
      if (decimalIndex > -1) {
        if (decimalIndex === value.length - 1) {
          message = 'Please enter a valid decimal value.';
        } else {
          message = '';
        }
      }
    }
    return message;
  }
}
